import type { ApiEvent } from '@/api/types';

/**
 * §8 спеки. Чистая структура, никакого React.
 *
 * Д-Ф7: ключ дедупликации ОТДАЁТ СЕРВЕР полем `dedup_key`. Его считает
 * app/intelligence/policy.py::dedup_key_for — оффер + ценовое ведро 3% + день,
 * то есть ровно то, что §8 и требует, и ровно тот же код, что у Telegram.
 *
 * Формула на клиент НЕ переносится (Global Constraint 15): два независимых
 * источника истины неизбежно разъезжаются, и расхождение проявилось бы как
 * разное поведение веба и бота. Поэтому здесь нет и не может быть вычисления
 * ценового ведра — только чтение готового поля.
 *
 * Контракт §3.10: `dedup_key` — обязательное непустое поле (String, не None).
 * Пустая строка теоретически возможна только у события без оффера; на этот
 * случай ключ откатывается на собственный id события, чтобы разные события
 * не склеивались в один баннер.
 */

export interface BannerState {
  active: ApiEvent | null;
  pending: ApiEvent[];
  /** Наибольший id, который уже проходил через очередь. */
  lastSeenId: number;
}

export function emptyQueue(lastSeenId = 0): BannerState {
  return { active: null, pending: [], lastSeenId };
}

function dedupKey(event: ApiEvent): string {
  return event.dedup_key || `event:${event.id}`;
}

export function enqueue(state: BannerState, events: ApiEvent[]): BannerState {
  const fresh = events
    .filter((event) => event.id > state.lastSeenId)
    .sort((a, b) => a.id - b.id);

  if (fresh.length === 0) return state;

  const taken = new Set<string>();
  if (state.active) taken.add(dedupKey(state.active));
  for (const event of state.pending) taken.add(dedupKey(event));

  const accepted: ApiEvent[] = [];
  for (const event of fresh) {
    const key = dedupKey(event);
    if (taken.has(key)) continue;
    taken.add(key);
    accepted.push(event);
  }

  const lastSeenId = Math.max(state.lastSeenId, ...fresh.map((event) => event.id));

  if (!state.active) {
    const [first, ...rest] = accepted;
    return { active: first ?? null, pending: [...state.pending, ...rest], lastSeenId };
  }

  return { active: state.active, pending: [...state.pending, ...accepted], lastSeenId };
}

export function dismiss(state: BannerState): BannerState {
  const [next, ...rest] = state.pending;
  return { active: next ?? null, pending: rest, lastSeenId: state.lastSeenId };
}
